/**
 * Consumer Loans list — borrower-facing list of all loans (active, in-flight,
 * closed). Built to the borrower-portal redesign spec (see
 * /tmp/design-pass/borrower/specs/loans.md):
 *
 *   - 5-col KPI strip (in progress / under review / offers·closing /
 *     funded·active / total exposure)
 *   - Tab strip (all / in-progress / under-review / approved / closed) with
 *     row counts as badges
 *   - "Astor sorted" chip + ghost sort affordance
 *   - 8-col table (Application # / Type / Amount / Status / Submitted /
 *     Astor next-step / Last updated / chevron)
 *   - SplitPanel-driven detail panel for quick-look + withdraw
 *
 * Backend wiring is unchanged from PR #856 — same `useMySubmissions` +
 * `useCases` + `useCasesByEntity` hooks, same merged-submission/case shape,
 * same `useWithdrawApplication` mutation. Only the presentation layer is
 * redesigned. Entity-filter banner and withdraw confirm dialog are kept.
 */

import AppsIcon from '@mui/icons-material/Apps';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HourglassIcon from '@mui/icons-material/HourglassEmpty';
import InfoIcon from '@mui/icons-material/Info';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReviewIcon from '@mui/icons-material/RateReview';
import SortIcon from '@mui/icons-material/Sort';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlassAlert,
  GlassButton,
  GlassCard,
  GlassChip,
  GlassIconButton,
  GlassTabs,
  GlassTextField,
  iconSizes,
  PanelAttributeList,
  PanelSection,
  ShimmerLoader,
  SplitPanel,
  SplitPanelContent,
  SplitPanelFooter,
  SplitPanelHeader,
  Stack,
  Typography,
  useI18n,
  useSplitPanel,
} from '@ops-matrix/design-system-ui';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { AstorAvatar } from '../../components/loans/AstorAvatar';
import { LoanKpiTile } from '../../components/loans/LoanKpiTile';
import { LoanStatusChip, LoanTypeChip } from '../../components/loans/LoanRowCard';
import { useConsumerType } from '../../contexts/ConsumerTypeContext';
import { useCases } from '../../hooks';
import { useCasesByEntity, useMyBorrowingEntity, useMySubmissions, useWithdrawApplication } from '../../lib/trpc/hooks';

// ---------- Domain types (merged submission + case shape) ----------

interface ApplicationItem {
  id: string;
  type: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  source: 'submission' | 'case';
  submissionNumber?: string;
  loanAmount?: number;
  propertyAddress?: string;
  convertedToCaseId?: string;
  caseNumber?: string;
}

type LoanFilterTab = 'all' | 'in_progress' | 'under_review' | 'approved' | 'closed';

/** Spec-mapped consumer-facing stage bucket */
type LoanStageBucket = 'in_progress' | 'under_review' | 'offers_closing' | 'funded_active' | 'closed' | 'other';

function getDisplayNumber(item: ApplicationItem): string {
  if (item.caseNumber) return item.caseNumber;
  if (item.submissionNumber) return item.submissionNumber;
  return item.id.slice(0, 8);
}

function bucketFor(status: string | undefined | null): LoanStageBucket {
  switch (status?.toLowerCase()) {
    case 'draft':
    case 'in_progress':
      return 'in_progress';
    case 'new':
    case 'submitted':
    case 'pending':
    case 'pending_review':
    case 'under_review':
    case 'reviewing':
    case 'qualified':
      return 'under_review';
    case 'pending_approval':
    case 'closing':
    case 'approval':
    case 'term_sheet':
      return 'offers_closing';
    case 'approved':
    case 'converted':
    case 'funded':
    case 'completed':
    case 'active':
    case 'servicing':
      return 'funded_active';
    case 'rejected':
    case 'closed':
    case 'withdrawn':
    case 'cancelled':
      return 'closed';
    default:
      return 'other';
  }
}

/** Returns the spec-matched "Astor next-step" copy for a loan. */
function astorNextStep(item: ApplicationItem, t: (k: string, vars?: Record<string, unknown>) => string): string {
  const bucket = bucketFor(item.status);
  switch (bucket) {
    case 'in_progress':
      return t('consumer.loans.astorNextStep.finishApplication', {
        defaultValue: 'Astor: finish the last 3 fields',
      });
    case 'under_review':
      return t('consumer.loans.astorNextStep.awaitingLender', {
        defaultValue: 'Astor: waiting on the lender — nothing to do',
      });
    case 'offers_closing':
      return t('consumer.loans.astorNextStep.reviewOffer', {
        defaultValue: 'Astor: review the offer when you have a moment',
      });
    case 'funded_active':
      return t('consumer.loans.astorNextStep.staySteady', {
        defaultValue: 'Astor: payments on track — I will flag anything',
      });
    case 'closed':
      return t('consumer.loans.astorNextStep.archived', { defaultValue: 'Astor: archived — open to revisit' });
    default:
      return t('consumer.loans.astorNextStep.checking', { defaultValue: 'Astor: checking…' });
  }
}

// ---------- Component ----------

export default function CaseListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useI18n();
  const { config } = useConsumerType();
  const { terminology } = config;

  const [activeTab, setActiveTab] = useState<LoanFilterTab>('all');
  const [search, setSearch] = useState('');
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawTarget, setWithdrawTarget] = useState<ApplicationItem | null>(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawAlert, setWithdrawAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const withdrawMutation = useWithdrawApplication();

  // Entity filter (Issue #1199) — preserved verbatim
  const entityIdFilter = searchParams.get('entityId');
  const { data: entityBorrowers } = useCasesByEntity(entityIdFilter);
  const { data: filterEntity } = useMyBorrowingEntity(entityIdFilter);

  const entityCaseIds = useMemo(() => {
    if (!entityIdFilter || !entityBorrowers?.data) return null;
    return new Set(entityBorrowers.data.map((b: { caseId: string }) => b.caseId));
  }, [entityIdFilter, entityBorrowers]);

  const clearEntityFilter = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('entityId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Fetch both submissions and cases
  const {
    data: submissionsResponse,
    isPending: submissionsPending,
    error: submissionsError,
  } = useMySubmissions({ page: 1, limit: 100 });

  const {
    data: casesResponse,
    isPending: casesPending,
    error: casesError,
  } = useCases(1, 100, { refetchInterval: 60_000, refetchOnWindowFocus: true });

  const isPending = submissionsPending || casesPending;
  const error = submissionsError || casesError;

  // Merge submissions + cases — unchanged from prior implementation
  const applications: ApplicationItem[] = useMemo(() => {
    const items: ApplicationItem[] = [];
    const seenCaseIds = new Set<string>();
    const caseNumberMap = new Map<string, string>();
    if (casesResponse?.cases) {
      for (const c of casesResponse.cases as Array<{ id: string; caseNumber?: string }>) {
        if (c.caseNumber) caseNumberMap.set(c.id, c.caseNumber);
      }
    }
    if (submissionsResponse?.submissions) {
      for (const s of submissionsResponse.submissions) {
        if (s.intakeType === 'partner_onboarding') continue;
        if (s.convertedToCaseId) seenCaseIds.add(s.convertedToCaseId);
        items.push({
          id: s.id,
          type: s.intakeType || s.templateName || 'Application',
          status: s.status || 'submitted',
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
          source: 'submission',
          submissionNumber: s.submissionNumber,
          loanAmount: s.loanAmount,
          propertyAddress: s.propertyAddress
            ? `${s.propertyAddress}${s.propertyCity ? `, ${s.propertyCity}` : ''}${
                s.propertyState ? `, ${s.propertyState}` : ''
              }`
            : undefined,
          convertedToCaseId: s.convertedToCaseId,
          caseNumber: s.convertedToCaseId ? caseNumberMap.get(s.convertedToCaseId) : undefined,
        });
      }
    }
    if (casesResponse?.cases) {
      for (const c of casesResponse.cases as Array<{
        id: string;
        type: string;
        status: string;
        createdAt: string;
        updatedAt: string;
        caseNumber?: string;
      }>) {
        if (!seenCaseIds.has(c.id)) {
          items.push({
            id: c.id,
            type: c.type || 'Case',
            status: c.status || 'open',
            createdAt: c.createdAt,
            updatedAt: c.updatedAt,
            source: 'case',
            caseNumber: c.caseNumber,
          });
        }
      }
    }
    items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return items;
  }, [submissionsResponse, casesResponse]);

  // Apply entity filter
  const entityFiltered = useMemo(() => {
    if (!entityCaseIds) return applications;
    return applications.filter((a) => {
      if (a.convertedToCaseId && entityCaseIds.has(a.convertedToCaseId)) return true;
      if (a.source === 'case' && entityCaseIds.has(a.id)) return true;
      return false;
    });
  }, [applications, entityCaseIds]);

  // KPI counts (spec strip)
  const kpi = useMemo(() => {
    const counts = { inProgress: 0, underReview: 0, offersClosing: 0, fundedActive: 0, totalExposure: 0 };
    for (const a of entityFiltered) {
      const b = bucketFor(a.status);
      if (b === 'in_progress') counts.inProgress++;
      else if (b === 'under_review') counts.underReview++;
      else if (b === 'offers_closing') counts.offersClosing++;
      else if (b === 'funded_active') {
        counts.fundedActive++;
        counts.totalExposure += a.loanAmount ?? 0;
      }
    }
    return counts;
  }, [entityFiltered]);

  // Per-tab counts (for badge labels)
  const tabCounts = useMemo(() => {
    const c = { all: entityFiltered.length, in_progress: 0, under_review: 0, approved: 0, closed: 0 };
    for (const a of entityFiltered) {
      const b = bucketFor(a.status);
      if (b === 'in_progress') c.in_progress++;
      else if (b === 'under_review') c.under_review++;
      else if (b === 'offers_closing' || b === 'funded_active') c.approved++;
      else if (b === 'closed') c.closed++;
    }
    return c;
  }, [entityFiltered]);

  // Tab filter
  const tabFiltered = useMemo(() => {
    if (activeTab === 'all') return entityFiltered;
    return entityFiltered.filter((a) => {
      const b = bucketFor(a.status);
      if (activeTab === 'approved') return b === 'offers_closing' || b === 'funded_active';
      return b === activeTab;
    });
  }, [entityFiltered, activeTab]);

  // Search filter (local)
  const filtered = useMemo(() => {
    if (!search) return tabFiltered;
    const q = search.toLowerCase();
    return tabFiltered.filter(
      (a) =>
        a.type?.toLowerCase().includes(q) ||
        a.status?.toLowerCase().includes(q) ||
        a.submissionNumber?.toLowerCase().includes(q) ||
        a.caseNumber?.toLowerCase().includes(q) ||
        a.propertyAddress?.toLowerCase().includes(q),
    );
  }, [tabFiltered, search]);

  // Formatters
  const formatDate = (s?: string) => (s ? new Date(s).toLocaleDateString() : '—');
  const formatCurrency = (n?: number) =>
    n == null
      ? '—'
      : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
  const formatCompactCurrency = (n: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(n);

  const formatTypeLabel = (type: string) => type.replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

  const formatStatusLabel = (status: string) =>
    t(`application.status.${bucketFor(status)}`, { defaultValue: status?.replace(/_/g, ' ') || '' });

  // Withdraw flow
  const canWithdraw = (item: ApplicationItem) => {
    const b = bucketFor(item.status);
    return b === 'under_review';
  };

  const handleWithdrawClick = useCallback((item: ApplicationItem) => {
    setWithdrawTarget(item);
    setWithdrawReason('');
    setWithdrawDialogOpen(true);
  }, []);

  const handleWithdrawClose = useCallback(() => {
    setWithdrawDialogOpen(false);
    setWithdrawTarget(null);
    setWithdrawReason('');
  }, []);

  const handleWithdrawConfirm = useCallback(async () => {
    if (!withdrawTarget) return;
    const caseId = withdrawTarget.source === 'submission' ? withdrawTarget.convertedToCaseId : withdrawTarget.id;
    if (!caseId) return;
    try {
      await withdrawMutation.mutateAsync({ caseId, reason: withdrawReason || undefined });
      setWithdrawDialogOpen(false);
      setWithdrawTarget(null);
      setWithdrawReason('');
      setWithdrawAlert({ type: 'success', message: t('application.withdrawn') });
    } catch {
      setWithdrawAlert({ type: 'error', message: t('application.withdrawError') });
    }
  }, [withdrawTarget, withdrawReason, withdrawMutation, t]);

  // Detail panel via SplitPanel + URL sync
  const urlSync = useMemo(
    () => ({
      getPanelId: () => searchParams.get('panelId'),
      setPanelId: (id: string | null) => {
        const next = new URLSearchParams(searchParams);
        if (id) next.set('panelId', id);
        else next.delete('panelId');
        setSearchParams(next, { replace: true });
      },
    }),
    [searchParams, setSearchParams],
  );

  const findItemById = useCallback((id: string) => entityFiltered.find((a) => a.id === id), [entityFiltered]);

  const { selectedItem, openPanel, closePanel, panelProps } = useSplitPanel<ApplicationItem>({
    idField: 'id',
    urlSync,
    findItemById,
  });

  const handleViewFullDetails = useCallback(() => {
    if (!selectedItem) return;
    const targetId = selectedItem.convertedToCaseId || selectedItem.id;
    const stateSource = selectedItem.source === 'case' || selectedItem.convertedToCaseId ? 'case' : 'submission';
    navigate(`/applications/${targetId}`, { state: { source: stateSource } });
  }, [selectedItem, navigate]);

  // Header actions
  const headerActions = (
    <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
      <GlassButton variant="outlined" size="small" startIcon={<TuneIcon />}>
        {t('consumer.loans.actions.filter', { defaultValue: 'Filter' })}
      </GlassButton>
      <GlassButton
        variant="gradient"
        size="small"
        onClick={() => navigate('/applications/new')}
        data-astor-id="start-new-application"
      >
        {t('consumer.loans.actions.newLoan', { defaultValue: 'New loan' })}
      </GlassButton>
    </Stack>
  );

  // Panel content — quick-look summary + actions
  const panelContent = selectedItem ? (
    <>
      <SplitPanelHeader
        title={getDisplayNumber(selectedItem)}
        subtitle={formatTypeLabel(selectedItem.type)}
        onClose={closePanel}
      />
      <SplitPanelContent>
        <PanelSection title={t('common.status')} icon={<InfoIcon />} collapsible={false}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <LoanStatusChip label={formatStatusLabel(selectedItem.status)} status={selectedItem.status} />
            <LoanTypeChip label={formatTypeLabel(selectedItem.type)} type={selectedItem.type} />
          </Stack>
          <PanelAttributeList
            attributes={[
              {
                label: t('consumer.loans.col.id', { defaultValue: 'Application #' }),
                value: getDisplayNumber(selectedItem),
                copyable: true,
              },
              ...(selectedItem.loanAmount
                ? [
                    {
                      label: t('consumer.loans.col.amount', { defaultValue: 'Amount' }),
                      value: formatCurrency(selectedItem.loanAmount),
                    },
                  ]
                : []),
              ...(selectedItem.propertyAddress
                ? [
                    {
                      label: t('application.property'),
                      value: selectedItem.propertyAddress,
                    },
                  ]
                : []),
              {
                label: t('consumer.loans.col.submitted', { defaultValue: 'Submitted' }),
                value: formatDate(selectedItem.createdAt),
              },
              {
                label: t('consumer.loans.col.lastUpdated', { defaultValue: 'Last updated' }),
                value: formatDate(selectedItem.updatedAt),
              },
            ]}
          />
        </PanelSection>
        <PanelSection title={t('consumer.loans.col.astorNextStep', { defaultValue: 'Astor next-step' })}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <AstorAvatar size={20} />
            <Typography variant="body2">{astorNextStep(selectedItem, t)}</Typography>
          </Stack>
        </PanelSection>
      </SplitPanelContent>
      <SplitPanelFooter>
        <GlassButton variant="outlined" onClick={handleViewFullDetails}>
          {t('common.viewDetails')}
        </GlassButton>
        {canWithdraw(selectedItem) && (
          <GlassButton variant="outlined" color="error" onClick={() => handleWithdrawClick(selectedItem)}>
            {t('application.withdraw')}
          </GlassButton>
        )}
      </SplitPanelFooter>
    </>
  ) : null;

  if (error) {
    return (
      <PageTemplate variant="list" titleKey="navigation.loans" icon={AppsIcon} actions={headerActions}>
        <GlassAlert severity="error" visible={true}>
          {t('application.failedToLoad', { entity: terminology.recordPlural, error: error.message })}
        </GlassAlert>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      variant="list"
      titleKey="navigation.loans"
      description={t('consumer.loans.subtitle', {
        defaultValue: 'All your loans — active, in flight, and closed',
      })}
      icon={AppsIcon}
      actions={headerActions}
    >
      <Box sx={{ overflowX: 'hidden' }}>
        <SplitPanel
          panelOpen={panelProps.open}
          onPanelClose={closePanel}
          storageKey="consumer-loans-panel"
          panel={panelContent}
          panelAriaLabel={t('application.statusTracking')}
        >
          {/* Alerts */}
          {withdrawAlert && (
            <GlassAlert
              severity={withdrawAlert.type}
              visible={true}
              onClose={() => setWithdrawAlert(null)}
              sx={{ mb: 2 }}
            >
              {withdrawAlert.message}
            </GlassAlert>
          )}
          {entityIdFilter && (
            <GlassAlert
              severity="info"
              visible={true}
              icon={<TuneIcon />}
              action={
                <GlassIconButton size="small" onClick={clearEntityFilter}>
                  <CloseIcon fontSize="small" />
                </GlassIconButton>
              }
              sx={{ mb: 2 }}
            >
              {t('application.filteredByEntity', {
                entityName: (filterEntity as { legalName?: string } | undefined)?.legalName ?? entityIdFilter,
              })}
            </GlassAlert>
          )}

          {/* KPI strip — 5 columns */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
              gap: 1.5,
              mb: 2.25,
            }}
            data-astor-id="application-status-tracker"
          >
            <LoanKpiTile
              label={t('consumer.loans.kpi.inProgress', { defaultValue: 'In progress' })}
              value={kpi.inProgress}
              tone="amber"
              icon={HourglassIcon}
              loading={isPending}
            />
            <LoanKpiTile
              label={t('consumer.loans.kpi.underReview', { defaultValue: 'Under review' })}
              value={kpi.underReview}
              tone="blue"
              icon={ReviewIcon}
              loading={isPending}
            />
            <LoanKpiTile
              label={t('consumer.loans.kpi.offersClosing', { defaultValue: 'Offers / closing' })}
              value={kpi.offersClosing}
              tone="id"
              icon={HandshakeIcon}
              loading={isPending}
            />
            <LoanKpiTile
              label={t('consumer.loans.kpi.fundedActive', { defaultValue: 'Funded / active' })}
              value={kpi.fundedActive}
              tone="mint"
              icon={CheckCircleIcon}
              loading={isPending}
            />
            <LoanKpiTile
              label={t('consumer.loans.kpi.totalExposure', { defaultValue: 'Total exposure' })}
              value={formatCompactCurrency(kpi.totalExposure)}
              tone="orange"
              icon={MonetizationOnIcon}
              loading={isPending}
            />
          </Box>

          {/* Main panel — tabs + sort row + table */}
          <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ px: 2.25, pt: 1.5 }}>
              <GlassTabs
                tabs={[
                  {
                    value: 'all',
                    label: (
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <span>{t('consumer.loans.filter.all', { defaultValue: 'All' })}</span>
                        <GlassChip size="small" label={tabCounts.all} color="default" />
                      </Stack>
                    ),
                  },
                  {
                    value: 'in_progress',
                    label: (
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <span>{t('consumer.loans.filter.inProgress', { defaultValue: 'In progress' })}</span>
                        <GlassChip size="small" label={tabCounts.in_progress} color="warning" />
                      </Stack>
                    ),
                  },
                  {
                    value: 'under_review',
                    label: (
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <span>{t('consumer.loans.filter.underReview', { defaultValue: 'Under review' })}</span>
                        <GlassChip size="small" label={tabCounts.under_review} color="info" />
                      </Stack>
                    ),
                  },
                  {
                    value: 'approved',
                    label: (
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <span>{t('consumer.loans.filter.approved', { defaultValue: 'Approved' })}</span>
                        <GlassChip size="small" label={tabCounts.approved} color="success" />
                      </Stack>
                    ),
                  },
                  {
                    value: 'closed',
                    label: (
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <span>{t('consumer.loans.filter.closed', { defaultValue: 'Closed' })}</span>
                        <GlassChip size="small" label={tabCounts.closed} color="default" />
                      </Stack>
                    ),
                  },
                ]}
                value={activeTab}
                onChange={(_, v) => setActiveTab(v as LoanFilterTab)}
                size="small"
              />
            </Box>
            <Box
              sx={{
                px: 2.25,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                borderTop: '1px solid',
                borderColor: 'divider',
                flexWrap: 'wrap',
              }}
            >
              <GlassTextField
                size="small"
                placeholder={t('consumer.loans.search.placeholder', {
                  defaultValue: 'Search by number, type, or address…',
                })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ maxWidth: 360, flex: 1 }}
              />
              <Box sx={{ flex: 1 }} />
              <GlassChip
                size="small"
                color="success"
                label={t('consumer.loans.astorSorted', { defaultValue: 'Astor sorted' })}
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      ml: 0.75,
                    }}
                  />
                }
                sx={{ '& .MuiChip-icon': { color: 'inherit' } }}
              />
              <GlassButton variant="text" size="small" startIcon={<SortIcon />}>
                {t('consumer.loans.sort.newestFirst', { defaultValue: 'Newest first' })}
              </GlassButton>
            </Box>

            {/* Loan table — 8-col grid */}
            <LoanTable
              items={filtered}
              loading={isPending}
              onRowClick={openPanel}
              t={t}
              formatDate={formatDate}
              formatCurrency={formatCurrency}
              formatTypeLabel={formatTypeLabel}
              formatStatusLabel={formatStatusLabel}
              navigate={navigate}
            />
          </GlassCard>
        </SplitPanel>
      </Box>

      {/* Withdraw confirm dialog (P0 — Dialog allowed for confirmations only, §14) */}
      <Dialog open={withdrawDialogOpen} onClose={handleWithdrawClose} maxWidth="xs" fullWidth>
        <DialogTitle>{t('application.withdrawTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t('application.confirmWithdraw')}</Typography>
          <GlassTextField
            fullWidth
            multiline
            rows={3}
            placeholder={t('application.withdrawReason')}
            value={withdrawReason}
            onChange={(e) => setWithdrawReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={handleWithdrawClose} disabled={withdrawMutation.isPending}>
            {t('common.cancel')}
          </GlassButton>
          <GlassButton
            variant="gradient"
            color="error"
            onClick={handleWithdrawConfirm}
            disabled={withdrawMutation.isPending}
          >
            {withdrawMutation.isPending ? t('common.loading') : t('application.withdraw')}
          </GlassButton>
        </DialogActions>
      </Dialog>
    </PageTemplate>
  );
}

// ---------- Local table sub-component ----------

interface LoanTableProps {
  items: ApplicationItem[];
  loading: boolean;
  onRowClick: (item: ApplicationItem) => void;
  t: (k: string, vars?: Record<string, unknown>) => string;
  formatDate: (s?: string) => string;
  formatCurrency: (n?: number) => string;
  formatTypeLabel: (type: string) => string;
  formatStatusLabel: (status: string) => string;
  navigate: (to: string) => void;
}

function LoanTable({
  items,
  loading,
  onRowClick,
  t,
  formatDate,
  formatCurrency,
  formatTypeLabel,
  formatStatusLabel,
  navigate,
}: LoanTableProps) {
  const cols = '160px 80px 130px 140px 140px 1fr 130px 36px';

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <ShimmerLoader variant="circular" width={40} height={40} />
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          {t('application.noApplications')}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {t('application.noApplicationsDescription')}
        </Typography>
        <GlassButton variant="gradient" onClick={() => navigate('/applications/new')}>
          {t('application.startNew')}
        </GlassButton>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: cols,
          gap: 1.5,
          alignItems: 'center',
          px: 2.25,
          py: 1.25,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        {[
          'consumer.loans.col.id',
          'consumer.loans.col.type',
          'consumer.loans.col.amount',
          'consumer.loans.col.status',
          'consumer.loans.col.submitted',
          'consumer.loans.col.astorNextStep',
          'consumer.loans.col.lastUpdated',
        ].map((key, idx) => {
          const fallback = [
            'Application #',
            'Type',
            'Amount',
            'Status',
            'Submitted',
            'Astor next-step',
            'Last updated',
          ][idx];
          return (
            <Typography
              key={key}
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: '0.08em',
                lineHeight: 1.2,
              }}
            >
              {t(key, { defaultValue: fallback })}
            </Typography>
          );
        })}
        <Box />
      </Box>
      {/* Data rows */}
      {items.map((item) => (
        <ButtonBase
          key={item.id}
          onClick={() => onRowClick(item)}
          sx={{
            display: 'grid',
            gridTemplateColumns: cols,
            gap: 1.5,
            alignItems: 'center',
            px: 2.25,
            py: 1.5,
            width: '100%',
            textAlign: 'left',
            borderTop: '1px solid',
            borderColor: 'divider',
            minHeight: 56,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
            {item.caseNumber || item.submissionNumber || item.id.slice(0, 8)}
          </Typography>
          <LoanTypeChip label={formatTypeLabel(item.type)} type={item.type} />
          <Typography variant="body2" sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(item.loanAmount)}
          </Typography>
          <LoanStatusChip label={formatStatusLabel(item.status)} status={item.status} />
          <Typography variant="caption" color="textSecondary">
            {formatDate(item.createdAt)}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ minWidth: 0, alignItems: 'center' }}>
            <AstorAvatar size={18} />
            <Typography variant="body2" noWrap sx={{ color: 'text.primary' }}>
              {astorNextStep(item, t)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="textSecondary">
            {formatDate(item.updatedAt)}
          </Typography>
          <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: iconSizes.md }} />
        </ButtonBase>
      ))}
    </Box>
  );
}
